import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CompanyService } from '@app/features/companies/company.service';
import { ContractType, ContractTypes } from '@app/features/contracts/contract.model';
import {
  VALIDATION_REQUEST_TYPES,
  ValidationRequestType,
} from '@app/features/contracts/validation/validation-request.model';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { DocumentTag, DocumentTags } from '@app/features/documents/document.model';
import { merge } from 'rxjs';
import {
  CADENCE_MAX_DAYS,
  CADENCE_MIN_DAYS,
  CADENCE_MIN_REMINDERS,
  ContractRelation,
  contractRelationLabels,
  DesignableRole,
  DesignationScope,
  generalLineOf,
  NotificationDesignation,
  NotificationEventSetting,
  notificationEventLabels,
  NotificationSettingLine,
  NotificationSettingLinePayload,
  lineLabel,
  ReminderCadence,
  roleLabel,
  ReminderFrequency,
  reminderSchedule,
} from '../notification-setting.model';
import { NotificationSettingService } from '../notification-setting.service';

export interface NotificationLineDialogData {
  setting: NotificationEventSetting;
  /** Absente : une ligne particulière à créer. */
  line: NotificationSettingLine | null;
}

export type NotificationLineDialogResult = 'saved' | 'deleted';

type ProfileGroup = FormGroup<{
  selected: FormControl<boolean>;
  scope: FormControl<DesignationScope>;
}>;

const DEFAULT_CADENCE: ReminderCadence = {
  firstReminderDelay: 5,
  frequency: 'FIXED',
  frequencyInterval: 2,
  maxReminders: null,
};

const integer: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  control.value === null || Number.isInteger(control.value) ? null : { integer: true };

const businessDays = [
  Validators.required,
  Validators.min(CADENCE_MIN_DAYS),
  Validators.max(CADENCE_MAX_DAYS),
  integer,
];

/** Une ligne particulière précise au moins un critère ; sans critère, ce serait la générale. */
const atLeastOneCriterion: ValidatorFn = (group: AbstractControl): ValidationErrors | null =>
  Object.values(group.value as Record<string, unknown>).some((value) => value !== null)
    ? null
    : { noCriterion: true };

/**
 * Modifier une ligne du réglage d'un événement : ses critères, ses destinataires et, pour une
 * relance, sa cadence (RG-notification-reglage-des-destinataires, RG-relance-cadence-reglable).
 */
@Component({
  selector: 'app-notification-line-dialog',
  imports: [
    ReactiveFormsModule,
    DatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ValidationRequestTypeLabelPipe,
  ],
  templateUrl: './notification-line-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-line-dialog.scss'],
})
export class NotificationLineDialog {
  private readonly dialogRef =
    inject<MatDialogRef<NotificationLineDialog, NotificationLineDialogResult>>(MatDialogRef);
  private readonly notificationSettingService = inject(NotificationSettingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly data = inject<NotificationLineDialogData>(MAT_DIALOG_DATA);

  protected readonly setting = this.data.setting;
  protected readonly line = this.data.line;
  protected readonly isGeneral = this.line?.general ?? false;
  protected readonly hasCadence = this.setting.cadenceAdjustable;
  protected readonly relations = this.setting.allowedRelations;
  protected readonly designableRoles = this.setting.designableRoles;

  protected readonly companies = toSignal(inject(CompanyService).allCompanies$, {
    initialValue: [],
  });
  protected readonly contractTypes = ContractTypes;
  protected readonly validationTypes = VALIDATION_REQUEST_TYPES;
  protected readonly documentTags = DocumentTags.values;
  protected readonly documentTagLabels = DocumentTags.labels;
  protected readonly roleLabel = roleLabel;
  protected readonly relationLabels = contractRelationLabels;
  protected readonly eventLabel = notificationEventLabels[this.setting.event];
  protected readonly minDays = CADENCE_MIN_DAYS;
  protected readonly maxDays = CADENCE_MAX_DAYS;

  /** Le jour d'où le calendrier compte : une surveillance qui s'ouvrirait aujourd'hui. */
  protected readonly today = new Date(new Date().setHours(0, 0, 0, 0));

  readonly form = this.buildForm();

  protected saving = false;
  protected confirmingDelete = false;

  constructor() {
    if (this.hasCadence) {
      const cadence = this.form.controls.cadence.controls;
      this.syncCadenceControls();
      merge(cadence.frequency.valueChanges, cadence.limited.valueChanges)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.syncCadenceControls());
    }
  }

  protected get title(): string {
    if (!this.line) {
      return 'Ajouter une ligne particulière';
    }
    return this.isGeneral ? 'Modifier la ligne générale' : 'Modifier la ligne particulière';
  }

  protected get subtitle(): string {
    if (!this.line || this.isGeneral) {
      return this.eventLabel;
    }
    return `${this.eventLabel} · ${lineLabel(this.setting, this.line.id).toLowerCase()}`;
  }

  protected get nobody(): boolean {
    return this.designations().length === 0;
  }

  /** Les rappels qu'aurait une surveillance ouverte aujourd'hui, tant que la cadence est valide. */
  protected get schedule(): { date: Date; gap: number }[] | null {
    const cadence = this.cadence();
    if (!cadence || this.form.controls.cadence.invalid) {
      return null;
    }
    const dates = reminderSchedule(cadence, this.today);
    return dates.map((date, index) => ({
      date,
      gap: businessDaysBetween(index === 0 ? this.today : dates[index - 1], date),
    }));
  }

  protected get limited(): boolean {
    return this.form.controls.cadence.controls.limited.value;
  }

  protected get isFixed(): boolean {
    return this.form.controls.cadence.controls.frequency.value === 'FIXED';
  }

  save(): void {
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    const payload = this.payload();
    const request = this.line
      ? this.notificationSettingService.updateLine(this.line.id, payload)
      : this.notificationSettingService.createLine(this.setting.event, payload);
    request.subscribe({
      next: () => this.dialogRef.close('saved'),
      error: () => (this.saving = false),
    });
  }

  delete(): void {
    if (!this.line || this.isGeneral) {
      return;
    }
    if (!this.confirmingDelete) {
      this.confirmingDelete = true;
      return;
    }
    this.notificationSettingService.deleteLine(this.line.id).subscribe({
      next: () => this.dialogRef.close('deleted'),
      error: () => (this.confirmingDelete = false),
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private payload(): NotificationSettingLinePayload {
    const criteria = this.form.controls.criteria.getRawValue();
    return {
      companyId: this.isGeneral ? null : criteria.companyId,
      contractType: this.isGeneral ? null : criteria.contractType,
      validationType: this.isGeneral ? null : criteria.validationType,
      documentTag: this.isGeneral ? null : criteria.documentTag,
      designations: this.designations(),
      cadence: this.cadence(),
    };
  }

  private designations(): NotificationDesignation[] {
    const { profiles, relations } = this.form.getRawValue();
    return [
      ...this.designableRoles
        .filter((role) => profiles[role]?.selected)
        .map((role) => ({ role, scope: profiles[role].scope, relation: null })),
      ...this.relations
        .filter((relation) => relations[relation])
        .map((relation) => ({ role: null, scope: null, relation })),
    ];
  }

  private cadence(): ReminderCadence | null {
    if (!this.hasCadence) {
      return null;
    }
    const cadence = this.form.controls.cadence.getRawValue();
    return {
      firstReminderDelay: cadence.firstReminderDelay ?? CADENCE_MIN_DAYS,
      frequency: cadence.frequency,
      frequencyInterval: cadence.frequency === 'FIXED' ? cadence.frequencyInterval : null,
      maxReminders: cadence.limited ? cadence.maxReminders : null,
    };
  }

  /** L'intervalle n'a de sens que pour une fréquence fixe, le maximum que s'il est demandé. */
  private syncCadenceControls(): void {
    const cadence = this.form.controls.cadence.controls;
    toggle(cadence.frequencyInterval, cadence.frequency.value === 'FIXED');
    toggle(cadence.maxReminders, cadence.limited.value);
  }

  private buildForm() {
    const line = this.line;
    const designations = line?.designations ?? [];
    const profile = (role: DesignableRole) =>
      designations.find((designation) => designation.role === role);
    const cadence = line?.cadence ?? generalLineOf(this.setting)?.cadence ?? DEFAULT_CADENCE;

    const profiles: Record<string, ProfileGroup> = {};
    for (const role of this.designableRoles) {
      profiles[role] = new FormGroup({
        selected: new FormControl(!!profile(role), { nonNullable: true }),
        scope: new FormControl<DesignationScope>(profile(role)?.scope ?? 'CONTRACT_COMPANY', {
          nonNullable: true,
        }),
      });
    }
    const relations: Record<string, FormControl<boolean>> = {};
    for (const relation of this.relations) {
      relations[relation] = new FormControl(
        designations.some((designation) => designation.relation === relation),
        { nonNullable: true },
      );
    }

    const form = new FormGroup({
      criteria: new FormGroup(
        {
          companyId: new FormControl<string | null>(line?.companyId ?? null),
          contractType: new FormControl<ContractType | null>(line?.contractType ?? null),
          validationType: new FormControl<ValidationRequestType | null>(
            line?.validationType ?? null,
          ),
          documentTag: new FormControl<DocumentTag | null>(line?.documentTag ?? null),
        },
        { validators: atLeastOneCriterion },
      ),
      profiles: new FormGroup(profiles),
      relations: new FormGroup(relations),
      cadence: new FormGroup({
        firstReminderDelay: new FormControl<number | null>(
          cadence.firstReminderDelay,
          businessDays,
        ),
        frequency: new FormControl<ReminderFrequency>(cadence.frequency, { nonNullable: true }),
        frequencyInterval: new FormControl<number | null>(
          cadence.frequencyInterval ?? DEFAULT_CADENCE.frequencyInterval,
          businessDays,
        ),
        limited: new FormControl(cadence.maxReminders !== null, { nonNullable: true }),
        maxReminders: new FormControl<number | null>(cadence.maxReminders, [
          Validators.required,
          Validators.min(CADENCE_MIN_REMINDERS),
          integer,
        ]),
      }),
    });
    if (this.isGeneral) {
      form.controls.criteria.disable({ emitEvent: false });
    }
    if (!this.hasCadence) {
      form.controls.cadence.disable({ emitEvent: false });
    }
    return form;
  }

  protected relationControl(relation: ContractRelation): FormControl<boolean> {
    return this.form.controls.relations.controls[relation];
  }
}

function toggle(control: AbstractControl, enabled: boolean): void {
  if (enabled) {
    control.enable({ emitEvent: false });
  } else {
    control.disable({ emitEvent: false });
  }
}

function businessDaysBetween(from: Date, to: Date): number {
  let count = 0;
  const date = new Date(from.getTime());
  while (date < to) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      count++;
    }
  }
  return count;
}
